import React, {useEffect, useRef} from 'react'
import {Button, Modal, Spinner} from 'react-bootstrap'
import styles from './ImpactModal.module.css'
import {analyticsService} from '../../services/analyticsService'

interface ImpactModalProps {
    show: boolean
    onHide: () => void
    title?: string
    headerLeft?: React.ReactNode
    actionLabel?: string
    onAction?: () => void | Promise<void>
    actionDisabled?: boolean
    actionLoading?: boolean
    cancelLabel?: string
    onCancel?: () => void
    hideFooter?: boolean
    size?: 'sm' | 'lg' | 'xl'
    backdrop?: 'static' | boolean
    keyboard?: boolean
    children: React.ReactNode
}

const ImpactModal: React.FC<ImpactModalProps> = ({
    show,
    onHide,
    title,
    headerLeft,
    actionLabel,
    onAction,
    actionDisabled = false,
    actionLoading = false,
    cancelLabel,
    onCancel,
    hideFooter = false,
    size,
    backdrop = true,
    keyboard = true,
    children
}) => {
    const modalName = title || 'unknown'
    const wasShownRef = useRef(false)

    useEffect(() => {
        if (show && !wasShownRef.current) {
            wasShownRef.current = true
            analyticsService.track('navigation.modal_opened', {modal_name: modalName})
        } else if (!show && wasShownRef.current) {
            wasShownRef.current = false
        }
    }, [show, modalName])

    const handleAction = async (): Promise<void> => {
        if (onAction) {
            analyticsService.track('navigation.modal_closed', {modal_name: modalName, action: 'confirm'})
            await onAction()
        }
    }

    const handleHide = () => {
        analyticsService.track('navigation.modal_closed', {modal_name: modalName, action: 'dismiss'})
        onHide()
    }

    const handleCancel = () => {
        analyticsService.track('navigation.modal_closed', {modal_name: modalName, action: 'cancel'})
        if (onCancel) {
            onCancel()
        } else {
            onHide()
        }
    }

    return (
        <Modal
            show={show}
            onHide={handleHide}
            fullscreen="sm-down"
            centered
            size={size}
            backdrop={backdrop}
            keyboard={keyboard && !actionLoading}
            className={styles.modal}
        >
            <Modal.Header closeButton={!actionLoading} className={styles.header}>
                {headerLeft}
                {title && (
                    <span className={styles.title}>{title}</span>
                )}
            </Modal.Header>

            <Modal.Body className={styles.body}>
                {children}
            </Modal.Body>

            {!hideFooter && (actionLabel || cancelLabel) && (onAction || onCancel) && (
                <Modal.Footer className={styles.footer}>
                    {cancelLabel && (
                        <Button
                            className={styles.cancelButton}
                            onClick={handleCancel}
                            disabled={actionLoading}
                            variant="secondary"
                        >
                            {cancelLabel}
                        </Button>
                    )}
                    {actionLabel && onAction && (
                        <Button
                            className={styles.actionButton}
                            onClick={handleAction}
                            disabled={actionDisabled || actionLoading}
                        >
                            {actionLoading ? (
                                <>
                                    <Spinner size="sm" animation="border" className="me-2"/>
                                </>
                            ) : (
                                actionLabel
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            )}
        </Modal>
    )
}

export default ImpactModal
